<script lang="ts" setup>
import { computed, inject, ref, watch } from "vue";

import { getPropertyDisplayTitle, getShownPropertyWithConfigList } from "~/common/properties";
import DartboardEditor from "~/components/dumb/DartboardEditor.vue";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import DropdownMenuItemContent from "~/components/dumb/DropdownMenuItemContent.vue";
import { DROPDOWN_REGISTER_SUBDROPDOWN_STATE } from "~/constants/injection";
import { EditIcon } from "~/icons";
import { DropdownMenuItemKind, EditorMode, Placement } from "~/shared/enums";
import type { Property, PropertyConfig, PropertyConfigContextMenu, Task } from "~/shared/types";
import { usePageStore } from "~/stores";

const props = defineProps<{
  targetTask: Task;
  selectedTasks: Task[];
  itemIndex?: number;
}>();

const dropdownMenuRef = ref<InstanceType<typeof DropdownMenu> | null>(null);

const isOpen = computed(() => dropdownMenuRef.value?.isOpen ?? false);

const registerSubdropdownState = inject(DROPDOWN_REGISTER_SUBDROPDOWN_STATE, null);

watch(isOpen, (newValue) => {
  if (registerSubdropdownState && props.itemIndex !== undefined) {
    registerSubdropdownState(props.itemIndex, newValue);
  }
});

const pageStore = usePageStore();

const plural = computed(() => (props.selectedTasks.length > 1 ? "s" : ""));
const sections = computed(() => [
  {
    title: "Properties",
    items: [
      {
        title: "Dartboard",
        component: DartboardEditor,
        kind: DropdownMenuItemKind.COMPONENT,
        componentArgs: {
          tasks: props.selectedTasks,
          editorMode: EditorMode.CONTEXT_MENU,
        },
      },
      ...getShownPropertyWithConfigList()
        .filter(
          (e): e is [Property, PropertyConfig & { contextMenu: PropertyConfigContextMenu<Property["kind"]> }] =>
            !!e[1].contextMenu
        )
        .map(([property, propertyConfig]) => {
          const contextMenu = propertyConfig.contextMenu();
          return {
            title: getPropertyDisplayTitle(property).toLowerCase(),
            icon: propertyConfig.icon,
            component: contextMenu.component,
            kind: DropdownMenuItemKind.COMPONENT,
            componentArgs: contextMenu.getValues(property, props.targetTask, props.selectedTasks),
          };
        }),
    ],
  },
]);
</script>

<template>
  <DropdownMenu
    ref="dropdownMenuRef"
    :sections="sections"
    :placement="Placement.RIGHT_TOP"
    mobile-include-back
    :prevent-close-on-select="pageStore.isMobile">
    <DropdownMenuItemContent :icon="EditIcon" :title="`Change task${plural}`" is-submenu />
  </DropdownMenu>
</template>
